import type { paperStrategy } from '@whaletail/signal-engine';
import { formatUsd } from '../../lib/format';

/**
 * Where a shadow replay's bankroll stands, in the Track record's words:
 * "Run 3 · 2 busts · $127.45 left · $512.79 paid in". A bust is a bankroll
 * too small for the next bet, topped back up to the starting pool.
 */
export function shadowRunsLine(
  summary: paperStrategy.ShadowPolicyDollarPnlSummary,
): string {
  if (summary.runCount === 0) return 'No bets yet';
  return [
    `Run ${summary.runCount}`,
    summary.busts === 0
      ? 'no busts'
      : summary.busts === 1
        ? '1 bust'
        : `${summary.busts} busts`,
    `${formatUsd(summary.endingPool)} left`,
    `${formatUsd(summary.deposited)} paid in`,
  ].join(' · ');
}

/** The note every shadow card shares on how its bankroll is kept. */
export const SHADOW_BUST_NOTE =
  'Each policy bets its own $200 bankroll. When it is too small to place the next bet, it is ' +
  'topped back up to $200 and a new run starts, so the totals count every bet, not just the first $200.';

/** Whether every row placed exactly the same bets (same count, stake and P&L). */
export function allRowsIdentical(
  rows: readonly paperStrategy.ShadowPolicyDollarPnlSummary[],
): boolean {
  if (rows.length < 2) return false;
  const [first, ...rest] = rows;
  return rest.every(
    (row) =>
      row.trades === first.trades &&
      Math.abs(row.stake - first.stake) < 0.005 &&
      Math.abs(row.pnl - first.pnl) < 0.005,
  );
}
